import {
  BoltIcon,
  ClockIcon,
  DocumentTextIcon,
  MapPinIcon,
  MegaphoneIcon,
  UserIcon,
} from '@heroicons/react/16/solid';
import React, { useEffect, useRef, useState } from 'react';

import { INTAKY_COLORS, INTAKY_FONT, statusToTierConfig } from './intaky-tokens';
import type { IntakyMapIntake } from './IntakyMap';

interface IntakyIntakeTableProps {
  intakes: IntakyMapIntake[];
  flashedIntakeIds: Set<string>;
  onIntakeClick?: (intake: IntakyMapIntake) => void;
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Grid template — same for header and rows
const GRID = 'minmax(200px,1fr) minmax(140px,180px) minmax(120px,160px) minmax(100px,130px) minmax(200px,2fr) minmax(70px,80px)';

const HEADER_CELL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: '700',
  color: INTAKY_COLORS.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

const ICON_STYLE: React.CSSProperties = {
  width: 12,
  height: 12,
  flexShrink: 0,
  opacity: 0.6,
};

interface NestedCircleProps {
  bg: string;
  mid: string;
  dot: string;
}

const NestedCircle: React.FC<NestedCircleProps> = ({ bg, mid, dot }) => (
  <div
    style={{
      width: 20,
      height: 20,
      borderRadius: '50%',
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <div
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: mid,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
    </div>
  </div>
);

/**
 * TickerText — if text overflows its container, slowly scrolls to reveal
 * the end, then scrolls back. Dynamic — no hardcoded char limit.
 */
const TickerText: React.FC<{
  text: string;
  style?: React.CSSProperties;
}> = ({ text, style }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;
    const diff = textEl.scrollWidth - container.clientWidth;
    setOverflow(diff > 2 ? diff : 0);
  }, [text]);

  return (
    <div
      ref={containerRef}
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        ...style,
      }}
    >
      <span
        ref={textRef}
        style={{
          display: 'inline-block',
          ...(overflow > 0
            ? {
                animation: 'intaky-ticker-bounce 6s ease-in-out infinite',
                animationDelay: '1s',
                '--ticker-offset': `-${overflow + 8}px`,
              } as React.CSSProperties
            : {}),
        }}
      >
        {text}
      </span>
    </div>
  );
};

const PAGE_SIZE = 50;

export const IntakyIntakeTable: React.FC<IntakyIntakeTableProps> = ({
  intakes,
  flashedIntakeIds,
  onIntakeClick,
}) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, intakes.length));
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [intakes.length]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [intakes]);

  const visibleIntakes = intakes.slice(0, visibleCount);
  const hasMore = visibleCount < intakes.length;

  if (intakes.length === 0) {
    return (
      <div
        style={{
          padding: '72px 28px',
          textAlign: 'center',
          color: INTAKY_COLORS.textMuted,
          fontSize: INTAKY_FONT.sizeMd,
          fontFamily: INTAKY_FONT.family,
        }}
      >
        No active leads — updates will appear here in real time.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: INTAKY_FONT.family, marginTop: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{ display: 'inline-block', minWidth: '100%' }}>
        {/* ── Column headers ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: GRID,
            gap: '0 24px',
            padding: '0 8px',
            height: 40,
            alignItems: 'center',
          }}
        >
          <div style={HEADER_CELL}><UserIcon style={ICON_STYLE} /> Client</div>
          <div style={HEADER_CELL}><DocumentTextIcon style={ICON_STYLE} /> Case Type</div>
          <div style={HEADER_CELL}><MegaphoneIcon style={ICON_STYLE} /> Referred By</div>
          <div style={HEADER_CELL}><MapPinIcon style={ICON_STYLE} /> Location</div>
          <div style={HEADER_CELL}><BoltIcon style={ICON_STYLE} /> Latest Update</div>
          <div style={{ ...HEADER_CELL, justifyContent: 'flex-end' }}><ClockIcon style={ICON_STYLE} /> When</div>
        </div>

        {/* Divider — full width of the table (inside scroll container) */}
        <div style={{ height: 1, background: INTAKY_COLORS.border }} />

        {/* ── Rows ── */}
        <div style={{ padding: '4px 0' }}>
          {visibleIntakes.map((intake, idx) => {
            const isFlashing = flashedIntakeIds.has(intake.id);
            const tierConfig = statusToTierConfig(intake.status);
            const isEven = idx % 2 === 1;
            const rowBg = isFlashing ? '#F0FDF6' : isEven ? '#F8FAFC' : 'white';

            return (
              <div
                key={intake.id}
                className="intaky-table-row"
                role="button"
                tabIndex={0}
                onClick={() => onIntakeClick?.(intake)}
                onKeyDown={(e) => { if (e.key === 'Enter') onIntakeClick?.(intake); }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID,
                  gap: '0 24px',
                  padding: '0 8px',
                  height: 48,
                  alignItems: 'center',
                  background: rowBg,
                  borderRadius: 10,
                  marginTop: idx > 0 ? 2 : 0,
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                  animation: isFlashing ? 'intaky-row-in 0.3s ease-out' : undefined,
                }}
              >
                {/* Client (with status dot) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
                  <NestedCircle bg={tierConfig.bg} mid={tierConfig.mid} dot={tierConfig.dot} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: INTAKY_COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                    {intake.client_display}
                  </span>
                </div>

                {/* Case type */}
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {intake.case_type || <span style={{ color: INTAKY_COLORS.textMuted }}>{'\u2014'}</span>}
                </div>

                {/* Referred by */}
                <div style={{ fontSize: 12, fontWeight: 500, color: INTAKY_COLORS.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {intake.referral_source || <span style={{ color: INTAKY_COLORS.textMuted }}>{'\u2014'}</span>}
                </div>

                {/* Location */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {intake.state ? (
                    <>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', background: tierConfig.bg, color: tierConfig.color, padding: '2px 7px', borderRadius: 5 }}>
                        {intake.state}
                      </span>
                      {intake.city && (
                        <span style={{ fontSize: 11, fontWeight: 500, color: INTAKY_COLORS.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {intake.city}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: INTAKY_COLORS.textMuted }}>{'\u2014'}</span>
                  )}
                </div>

                {/* Latest update */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1, overflow: 'hidden' }}>
                  {isFlashing && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: INTAKY_COLORS.statusGreenBg, color: INTAKY_COLORS.statusGreen, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 9999, letterSpacing: '0.03em', width: 'fit-content', marginBottom: 1 }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: INTAKY_COLORS.statusGreenDot, flexShrink: 0 }} />
                      New
                    </div>
                  )}
                  {(() => {
                    const rawHeadline = intake.last_activity_headline || '';
                    const headline = rawHeadline.replace(/\bAuto\s+/gi, '');
                    const rawDetail = intake.last_activity_detail || '';
                    const detail = rawDetail.replace(/\bAuto\s+/gi, '');
                    const combined = detail && headline
                      ? `${headline}: ${detail}`
                      : headline || detail;
                    if (!combined) {
                      return <span style={{ color: INTAKY_COLORS.textMuted, fontSize: 12 }}>No activity yet</span>;
                    }
                    return (
                      <TickerText
                        text={combined}
                        style={{
                          fontSize: 12,
                          color: isFlashing ? INTAKY_COLORS.textPrimary : '#374151',
                          fontWeight: isFlashing ? '600' : '500',
                          transition: 'color 1.5s ease',
                        }}
                      />
                    );
                  })()}
                </div>

                {/* When */}
                <div style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {timeAgo(intake.last_activity_at)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll sentinel + load-more */}
        <div ref={sentinelRef} style={{ height: 1 }} />
        {hasMore && (
          <div style={{ padding: '16px 0', textAlign: 'center', color: INTAKY_COLORS.textMuted, fontSize: INTAKY_FONT.sizeSm, fontWeight: '500' }}>
            Showing {visibleCount} of {intakes.length} leads
          </div>
        )}
      </div>
    </div>
  );
};
